import React, { useState, useEffect } from 'react';
import appScriptService from '../../utils/appScriptService';
import gcsService from '../../utils/gcsService';

const InterviewManager = () => {
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [selectedModule, setSelectedModule] = useState('전체');
  const [loading, setLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editForm, setEditForm] = useState({
    과목코드: '',
    토픽명: '',
    질문: '',
    예상키워드: '',
    모범답안: '',
    난이도: '중',
    영상: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('전체');

  // NCS 모듈 목록
  const modules = [
    { id: '전체', name: '전체 모듈' },
    { id: 'LM2001020230_프로그래밍언어응용', name: '프로그래밍 언어 응용' },
    { id: 'LM2001020231_프로그래밍언어활용', name: '프로그래밍 언어 활용' },
    { id: 'LM2001020224_화면설계', name: '화면설계' },
    { id: 'LM2001020225_화면구현', name: '화면구현' },
    { id: 'LM2001020211_서버프로그램구현', name: '서버프로그램구현' },
    { id: 'LM2001020227_애플리케이션테스트수행', name: '애플리케이션테스트수행' },
    { id: 'LM2001020205_데이터입출력구현', name: '데이터입출력구현' },
    { id: 'LM2001020206_통합구현', name: '통합구현' },
    { id: 'LM2001020214_애플리케이션배포', name: '애플리케이션배포' },
    { id: 'LM2001020212_인터페이스구현', name: '인터페이스구현' },
    { id: 'BE001_SPRING', name: 'Spring 백엔드' },
    { id: 'FE001_REACT', name: 'React 프론트엔드' },
    { id: 'HR001_COMMON', name: '공통 면접질문' },
  ];

  useEffect(() => {
    loadInterviewQuestions();
  }, [selectedModule]);

  // 면접 질문 데이터 로드
  const loadInterviewQuestions = async () => {
    setLoading(true);
    try {
      const filters = selectedModule === '전체' ? {} : { 과목코드: selectedModule };
      const data = await appScriptService.getData('면접질문', filters);
      const questionsData = Array.isArray(data) ? data : (data?.data || []);

      // GCS 서비스를 사용해 영상 URL을 Signed URL로 변환
      const processedQuestions = await gcsService.processInterviewQuestions(questionsData);

      setInterviewQuestions(processedQuestions);
    } catch (error) {
      console.error('면접 질문 로드 실패:', error);
      alert('데이터 로드에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 질문 편집 시작
  const startEdit = (question) => {
    setEditingQuestion(question);
    setEditForm({
      과목코드: question.과목코드 || '',
      토픽명: question.토픽명 || '',
      질문: question.질문 || '',
      예상키워드: Array.isArray(question.예상키워드) ? question.예상키워드.join(', ') : (question.예상키워드 || ''),
      모범답안: question.모범답안 || '',
      난이도: question.난이도 || '중',
      영상: question.영상 || ''
    });
  };

  // 질문 수정 저장
  const saveEdit = async () => {
    try {
      setLoading(true);

      const updatedData = {
        ...editForm,
        예상키워드: editForm.예상키워드.split(',').map(k => k.trim()).filter(k => k)
      };

      await appScriptService.updateData('면접질문', editingQuestion.rowId, updatedData);

      setEditingQuestion(null);
      setEditForm({
        과목코드: '',
        토픽명: '',
        질문: '',
        예상키워드: '',
        모범답안: '',
        난이도: '중',
        영상: ''
      });

      await loadInterviewQuestions();
      alert('질문이 수정되었습니다.');
    } catch (error) {
      console.error('질문 수정 실패:', error);
      alert('수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 질문 삭제
  const deleteQuestion = async (question) => {
    if (!window.confirm('정말로 이 질문을 삭제하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      await appScriptService.deleteData('면접질문', question.rowId);
      await loadInterviewQuestions();
      alert('질문이 삭제되었습니다.');
    } catch (error) {
      console.error('질문 삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 새 질문 추가
  const addNewQuestion = async () => {
    if (!editForm.질문.trim()) {
      alert('질문을 입력해주세요.');
      return;
    }

    try {
      setLoading(true);

      const newQuestionData = {
        과목코드: editForm.과목코드 || selectedModule === '전체' ? 'LM2001020230_프로그래밍언어응용' : selectedModule,
        토픽명: editForm.토픽명.trim(),
        질문: editForm.질문.trim(),
        예상키워드: editForm.예상키워드.split(',').map(k => k.trim()).filter(k => k),
        모범답안: editForm.모범답안.trim(),
        난이도: editForm.난이도,
        영상: editForm.영상.trim(),
        생성일시: new Date().toISOString(),
        사용횟수: 0
      };

      await appScriptService.addData('면접질문', [newQuestionData]);

      setEditForm({
        과목코드: '',
        토픽명: '',
        질문: '',
        예상키워드: '',
        모범답안: '',
        난이도: '중',
        영상: ''
      });

      await loadInterviewQuestions();
      alert('새 질문이 추가되었습니다.');
    } catch (error) {
      console.error('질문 추가 실패:', error);
      alert('추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 필터링된 질문 목록
  const filteredQuestions = interviewQuestions.filter(question => {
    const matchesSearch = !searchTerm ||
      question.질문?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.토픽명?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDifficulty = difficultyFilter === '전체' || question.난이도 === difficultyFilter;

    return matchesSearch && matchesDifficulty;
  });

  // 통계 계산
  const stats = {
    total: interviewQuestions.length,
    byDifficulty: {
      상: interviewQuestions.filter(q => q.난이도 === '상').length,
      중: interviewQuestions.filter(q => q.난이도 === '중').length,
      하: interviewQuestions.filter(q => q.난이도 === '하').length,
    },
    withVideo: interviewQuestions.filter(q => q.영상).length,
    byModule: modules.slice(1).reduce((acc, module) => {
      acc[module.name] = interviewQuestions.filter(q => q.과목코드 === module.id).length;
      return acc;
    }, {})
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            🎤 교사용 면접 질문 관리
          </h1>
          <p className="text-gray-600">
            NCS 모듈별 면접 질문을 관리하고 편집할 수 있습니다.
          </p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">전체 질문</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <div className="text-3xl">📋</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">영상 질문</p>
                <p className="text-2xl font-bold text-purple-600">{stats.withVideo}</p>
              </div>
              <div className="text-3xl">🎥</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">난이도별</p>
                <div className="flex gap-2 text-sm">
                  <span className="text-red-600">상:{stats.byDifficulty.상}</span>
                  <span className="text-yellow-600">중:{stats.byDifficulty.중}</span>
                  <span className="text-green-600">하:{stats.byDifficulty.하}</span>
                </div>
              </div>
              <div className="text-3xl">📊</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">현재 모듈</p>
                <p className="text-lg font-semibold text-gray-800">
                  {modules.find(m => m.id === selectedModule)?.name || '전체'}
                </p>
              </div>
              <div className="text-3xl">🎯</div>
            </div>
          </div>
        </div>

        {/* 필터 및 검색 */}
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">모듈 선택</label>
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {modules.map(module => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">난이도 필터</label>
              <select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="전체">전체 난이도</option>
                <option value="상">상</option>
                <option value="중">중</option>
                <option value="하">하</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">검색</label>
              <input
                type="text"
                placeholder="질문이나 토픽명으로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={loadInterviewQuestions}
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '로딩...' : '🔄 새로고침'}
              </button>
            </div>
          </div>
        </div>

        {/* 새 질문 추가 폼 */}
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">➕ 새 질문 추가</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">과목코드</label>
              <select
                value={editForm.과목코드}
                onChange={(e) => setEditForm({...editForm, 과목코드: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">선택하세요</option>
                {modules.slice(1).map(module => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">토픽명</label>
              <input
                type="text"
                value={editForm.토픽명}
                onChange={(e) => setEditForm({...editForm, 토픽명: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="토픽명을 입력하세요"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">질문</label>
              <textarea
                value={editForm.질문}
                onChange={(e) => setEditForm({...editForm, 질문: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-20 resize-none"
                placeholder="면접 질문을 입력하세요"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">예상키워드 (쉼표로 구분)</label>
              <input
                type="text"
                value={editForm.예상키워드}
                onChange={(e) => setEditForm({...editForm, 예상키워드: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="키워드1, 키워드2, 키워드3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">난이도</label>
              <select
                value={editForm.난이도}
                onChange={(e) => setEditForm({...editForm, 난이도: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="하">하</option>
                <option value="중">중</option>
                <option value="상">상</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">모범답안</label>
              <textarea
                value={editForm.모범답안}
                onChange={(e) => setEditForm({...editForm, 모범답안: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-20 resize-none"
                placeholder="모범답안을 입력하세요"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">영상 파일 경로 (선택사항)</label>
              <input
                type="text"
                value={editForm.영상}
                onChange={(e) => setEditForm({...editForm, 영상: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="video/example.mp4"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={addNewQuestion}
              disabled={loading || !editForm.질문.trim()}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              ➕ 질문 추가
            </button>
          </div>
        </div>

        {/* 질문 목록 */}
        <div className="bg-white rounded-xl shadow-lg">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-800">
              📋 면접 질문 목록 ({filteredQuestions.length}개)
            </h3>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">데이터를 불러오는 중...</p>
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                질문이 없습니다. 새 질문을 추가해보세요.
              </div>
            ) : (
              <div className="space-y-4">
                {filteredQuestions.map((question, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    {editingQuestion && editingQuestion === question ? (
                      // 편집 모드
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">과목코드</label>
                            <select
                              value={editForm.과목코드}
                              onChange={(e) => setEditForm({...editForm, 과목코드: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              {modules.slice(1).map(module => (
                                <option key={module.id} value={module.id}>
                                  {module.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">토픽명</label>
                            <input
                              type="text"
                              value={editForm.토픽명}
                              onChange={(e) => setEditForm({...editForm, 토픽명: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">질문</label>
                            <textarea
                              value={editForm.질문}
                              onChange={(e) => setEditForm({...editForm, 질문: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-20 resize-none"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">예상키워드</label>
                            <input
                              type="text"
                              value={editForm.예상키워드}
                              onChange={(e) => setEditForm({...editForm, 예상키워드: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">난이도</label>
                            <select
                              value={editForm.난이도}
                              onChange={(e) => setEditForm({...editForm, 난이도: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="하">하</option>
                              <option value="중">중</option>
                              <option value="상">상</option>
                            </select>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">모범답안</label>
                            <textarea
                              value={editForm.모범답안}
                              onChange={(e) => setEditForm({...editForm, 모범답안: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-20 resize-none"
                            />
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">영상 파일 경로</label>
                            <input
                              type="text"
                              value={editForm.영상}
                              onChange={(e) => setEditForm({...editForm, 영상: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={saveEdit}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            ✅ 저장
                          </button>
                          <button
                            onClick={() => setEditingQuestion(null)}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                          >
                            ❌ 취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      // 표시 모드
                      <div>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {modules.find(m => m.id === question.과목코드)?.name || question.과목코드}
                              </span>
                              <span className={`text-sm px-2 py-1 rounded text-white ${
                                question.난이도 === '상' ? 'bg-red-500' :
                                question.난이도 === '중' ? 'bg-yellow-500' : 'bg-green-500'
                              }`}>
                                {question.난이도}
                              </span>
                              {question.영상 && (
                                <span className="text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                  🎥 영상
                                </span>
                              )}
                            </div>
                            <h4 className="font-medium text-gray-800 mb-1">{question.토픽명}</h4>
                            <p className="text-gray-700 mb-3">{question.질문}</p>

                            <div className="space-y-2">
                              <div>
                                <span className="text-sm font-medium text-purple-700">예상키워드: </span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {(Array.isArray(question.예상키워드) ? question.예상키워드 : question.예상키워드?.split(',') || []).map((keyword, idx) => (
                                    <span key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                      {keyword.trim()}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <span className="text-sm font-medium text-blue-700">모범답안: </span>
                                <p className="text-sm text-gray-600 mt-1 bg-blue-50 p-2 rounded">
                                  {question.모범답안}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => startEdit(question)}
                              className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                            >
                              ✏️ 편집
                            </button>
                            <button
                              onClick={() => deleteQuestion(question)}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                              🗑️ 삭제
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewManager;